import { useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { Plus, Pen, Trash2, Phone, X as IconX, UserRound, GitBranch } from 'lucide-react';
import { motion } from 'framer-motion';
import { DeliveryVehicleIcon, VEHICLE_OPTIONS } from '../../components/DeliveryVehicleIcon';
import { CustomSelect } from '../../components/CustomSelect';
import { SideDrawer } from '../../components/SideDrawer';
import {
  effectiveBranchesOf, deliveryAgentsOf, findBranch,
  addDeliveryAgent, updateDeliveryAgent, deleteDeliveryAgent,
} from '../../data/store';
import { IconAction } from '../../components/RowActions';

const blankAgent = (orgId, branchId) => ({
  id: null, orgId, branchId: branchId || '', name: '', mobileNumber: '', vehicle: 'Bike', status: 'available',
});

const AgentModal = ({ draft, setDraft, branches, branchesEnabled, lockedBranchId, onClose, onSubmit, mode, accent }) => {
  const setField = (k, v) => setDraft({ ...draft, [k]: v });
  return (
    <SideDrawer onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="flex flex-col h-full">
        <header className="px-5 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg text-white flex items-center justify-center" style={{ background: accent }} aria-hidden="true">
              <UserRound size={18} />
            </div>
            <div>
              <h3 className="text-base font-extrabold text-[#0d0d0d]">{mode === 'edit' ? 'Edit agent' : 'New delivery agent'}</h3>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">{mode === 'edit' ? draft.name || '—' : 'Add to the delivery fleet'}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50">
            <IconX size={16} />
          </button>
        </header>

        <div className="px-5 py-5 flex-1 overflow-y-auto custom-scrollbar grid grid-cols-1 sm:grid-cols-2 gap-4 content-start">
          <label className="block space-y-1 sm:col-span-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Agent name <span className="text-rose-500">*</span></span>
            <input type="text" value={draft.name} onChange={(e) => setField('name', e.target.value)} placeholder="e.g. Theo Marsh"
              required maxLength={50}
              className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
          </label>
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Mobile Number <span className="text-rose-500">*</span></span>
            <input type="tel" value={draft.mobileNumber} onChange={(e) => {
                const val = e.target.value.replace(/\D/g, ''); // strictly digits only
                if (val.length <= 10) setField('mobileNumber', val);
              }} placeholder="9876543210"
              required pattern="^\d{10}$" title="Please enter exactly 10 digits" maxLength={10}
              className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]" />
          </label>
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Vehicle</span>
            <CustomSelect
              value={draft.vehicle}
              onChange={(v) => setField('vehicle', v)}
              options={VEHICLE_OPTIONS.map((v) => ({ value: v, label: v }))}
              className="w-full border-gray-100 bg-gray-50 text-xs font-medium normal-case tracking-normal py-2 px-3"
            />
          </label>
          {branchesEnabled && (
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Branch</span>
              <CustomSelect
                value={draft.branchId}
                onChange={(v) => setField('branchId', v)}
                disabled={!!lockedBranchId}
                options={branches.map((b) => ({ value: b.id, label: b.name }))}
                className="w-full border-gray-100 bg-gray-50 text-xs font-medium normal-case tracking-normal py-2 px-3 disabled:opacity-60"
              />
            </label>
          )}
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Availability</span>
            <CustomSelect
              value={draft.status}
              onChange={(v) => setField('status', v)}
              options={[
                { value: 'available', label: 'Available' },
                { value: 'busy', label: 'Busy' }
              ]}
              className="w-full border-gray-100 bg-gray-50 text-xs font-medium normal-case tracking-normal py-2 px-3"
            />
          </label>
        </div>

        <footer className="px-5 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3 shrink-0" style={{ background: 'var(--theme-secondary)' }}>
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Cancel</button>
          <button type="submit"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]"
            style={{ background: accent }}>
            {mode === 'edit' ? 'Save agent' : 'Add agent'}
          </button>
        </footer>
      </form>
    </SideDrawer>
  );
};

AgentModal.propTypes = {
  draft: PropTypes.object.isRequired,
  setDraft: PropTypes.func.isRequired,
  branches: PropTypes.arrayOf(PropTypes.object).isRequired,
  branchesEnabled: PropTypes.bool,
  lockedBranchId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  accent: PropTypes.string.isRequired,
};

export const DeliveryAgentsPanel = forwardRef(({ org, branchesEnabled, search, showNotification, canEdit, lockedBranchId }, ref) => {
  const [version, setVersion] = useState(0);
  const bump = () => setVersion((v) => v + 1);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [draft, setDraft] = useState(blankAgent(org.id));
  const [pendingDelete, setPendingDelete] = useState(null);

  const accent = org.theme.primary;
  const branches = useMemo(() => effectiveBranchesOf(org.id), [org.id]);

  const agents = useMemo(() => {
    let list = deliveryAgentsOf(org.id, lockedBranchId || null);
    if (search && search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(a => `${a.name} ${a.mobileNumber} ${a.vehicle}`.toLowerCase().includes(q));
    }
    return list;
  }, [org.id, lockedBranchId, version, search]);

  const openCreate = () => {
    setDraft(blankAgent(org.id, lockedBranchId || branches[0]?.id));
    setModalMode('create');
    setModalOpen(true);
  };
  const openEdit = (agent) => {
    setDraft({ ...agent });
    setModalMode('edit');
    setModalOpen(true);
  };
  const submit = () => {
    if (!draft.name.trim() || !draft.branchId) return;
    if (modalMode === 'create') {
      addDeliveryAgent(draft);
      if (showNotification) showNotification(`Added ${draft.name}`, 'success');
    } else if (draft.id) {
      updateDeliveryAgent(draft.id, draft);
      if (showNotification) showNotification(`Updated ${draft.name}`, 'success');
    }
    setModalOpen(false);
    bump();
  };
  const toggleStatus = (agent) => {
    updateDeliveryAgent(agent.id, { status: agent.status === 'available' ? 'busy' : 'available' });
    bump();
  };
  const confirmDelete = () => {
    if (!pendingDelete) return;
    deleteDeliveryAgent(pendingDelete.id);
    if (showNotification) showNotification(`Removed ${pendingDelete.name}`, 'success');
    setPendingDelete(null);
    bump();
  };

  useImperativeHandle(ref, () => ({ openCreate }));

  return (
    <div className="flex flex-col gap-4">
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 min-[1920px]:grid-cols-5 gap-4">
        {agents.map((agent) => {
          const branch = findBranch(agent.branchId);
          const available = agent.status === 'available';
          return (
            <motion.article key={agent.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="bg-white border border-[var(--theme-tertiary)] rounded-lg p-5 flex flex-col gap-3">
              <header className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-extrabold text-[#0d0d0d] truncate uppercase">{agent.name}</p>
                  <p className="text-[11px] font-medium text-slate-400 truncate mt-0.5">
                    {agent.mobileNumber || '—'}
                  </p>
                </div>
                <span className={`text-[11px] font-semibold uppercase tracking-[0.12em] ${available ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {available ? 'Available' : 'Busy'}
                </span>
              </header>
              <dl className="flex items-center gap-4 text-[11px]">
                <div className="inline-flex items-center gap-1.5 text-slate-600 font-bold">
                  <DeliveryVehicleIcon vehicle={agent.vehicle} size={13} aria-hidden="true" /> {agent.vehicle}
                </div>
                {branchesEnabled && (
                  <div className="inline-flex items-center gap-1.5 text-slate-600 font-bold min-w-0">
                    <GitBranch size={12} aria-hidden="true" /> <span className="truncate">{branch?.name || '—'}</span>
                  </div>
                )}
              </dl>
              {canEdit && (
                <footer className="flex items-center justify-end gap-2 mt-auto pt-2 border-t border-[var(--theme-tertiary)]">
                  <button type="button" onClick={() => toggleStatus(agent)}
                    className="mr-auto px-3 py-1.5 rounded-lg border border-[var(--theme-tertiary)] text-slate-600 hover:border-[var(--theme-primary-tint)] hover:text-[var(--theme-primary)] text-xs font-semibold uppercase tracking-[0.12em]">
                    Mark {available ? 'busy' : 'available'}
                  </button>
                  <IconAction icon={Pen} label={`Edit ${agent.name}`} title="Edit" onClick={() => openEdit(agent)} />
                  <IconAction icon={Trash2} tone="danger" label={`Remove ${agent.name}`} title="Remove" onClick={() => setPendingDelete(agent)} />
                </footer>
              )}
            </motion.article>
          );
        })}
        {agents.length === 0 && (
          <div className="col-span-full py-16 bg-white border border-dashed border-[var(--theme-tertiary)] rounded-lg text-center">
            <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">No delivery agents yet.</p>
            {canEdit && (
              <button type="button" onClick={openCreate} className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]" style={{ background: accent }}>
                <Plus size={12} /> Add the first agent
              </button>
            )}
          </div>
        )}
      </section>

      {modalOpen && (
        <AgentModal
          draft={draft}
          setDraft={setDraft}
          branches={branches}
          branchesEnabled={branchesEnabled}
          lockedBranchId={lockedBranchId}
          onClose={() => setModalOpen(false)}
          onSubmit={submit}
          mode={modalMode}
          accent={accent}
        />
      )}

      {pendingDelete && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(6px)' }}>
          <div className="w-full max-w-sm bg-white rounded-lg shadow-xl border border-rose-100 overflow-hidden">
            <div className="px-6 py-6 text-center">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center text-rose-500 mb-3" style={{ background: 'rgba(244,63,94,0.10)' }} aria-hidden="true">
                <Trash2 size={20} />
              </div>
              <h4 className="text-base font-extrabold text-[#0d0d0d]">Remove agent?</h4>
              <p className="mt-1 text-[11px] font-medium text-slate-500">
                <span className="font-bold text-rose-500">{pendingDelete.name}</span> will be removed from the delivery fleet.
              </p>
            </div>
            <div className="flex border-t border-rose-100">
              <button type="button" onClick={() => setPendingDelete(null)} className="flex-1 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={confirmDelete} className="flex-1 py-3 text-xs font-semibold uppercase tracking-[0.14em] bg-rose-500 text-white hover:bg-rose-600">Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

DeliveryAgentsPanel.displayName = 'DeliveryAgentsPanel';

DeliveryAgentsPanel.propTypes = {
  org: PropTypes.object.isRequired,
  branchesEnabled: PropTypes.bool,
  showNotification: PropTypes.func,
  canEdit: PropTypes.bool,
  lockedBranchId: PropTypes.string,
};
